import { Trans, useLingui } from '@lingui/react/macro';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import { useDuplicateTemplate } from 'modules/manager/ui/hooks/use-duplicate-template';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerDuplicateTemplateForm, {
  DUPLICATE_TEMPLATE_INITIAL_VALUES,
  DuplicateTemplateFormValues,
} from 'modules/manager/ui/components/forms/media-manager-duplicate-template-form';

interface Args {
  onDuplicated: (copy: MediaManagerTemplate) => void;
}

function useDuplicateTemplateModal({ onDuplicated }: Args) {
  const { t } = useLingui();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const { data: groups = [] } = useMediaManagerTemplateGroupsQuery({ tournamentId: tournamentId ?? 0 }, { skip: !tournamentId });
  const { duplicate, isDuplicating, error, resetError } = useDuplicateTemplate();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [templateGroupId, setTemplateGroupId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(
    (next: MediaManagerTemplate) => {
      resetError();
      setTemplate(next);
      setTemplateGroupId(groups[0]?.id ?? null);
      setIsOpen(true);
    },
    [resetError, groups],
  );
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: DuplicateTemplateFormValues) => {
      if (!template || templateGroupId == null) return;
      const copy = await duplicate(template.id, { name: values.name, templateGroupId });
      if (!copy) return;
      setIsOpen(false);
      onDuplicated(copy);
    },
    [template, templateGroupId, duplicate, onDuplicated],
  );

  return {
    open,
    close,
    canDuplicate: !!tournamentId && groups.length > 0,
    isDuplicating,
    modal: (
      <Modal show={isOpen} onClose={isDuplicating ? () => undefined : close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Дублировать шаблон</Trans>
        </UDText>
        <label className="mb-3 flex flex-col gap-2">
          <UDText variant="body-3" className="text-gray-400">
            <Trans>Группа</Trans>
          </UDText>
          <select
            className="h-10 rounded border border-gray-200 px-3"
            value={templateGroupId ?? ''}
            disabled={isDuplicating}
            onChange={(event) => setTemplateGroupId(event.target.value ? Number(event.target.value) : null)}
          >
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>
        <MediaManagerDuplicateTemplateForm
          key={template?.id ?? 'none'}
          initialValues={DUPLICATE_TEMPLATE_INITIAL_VALUES}
          sourceName={template?.name ?? t`Название шаблона`}
          isLoading={isDuplicating}
          errorMessage={error}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useDuplicateTemplateModal;
